import {
  collection, doc, getDocs, getDoc, updateDoc, deleteDoc, setDoc,
  query, where, orderBy, serverTimestamp, Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';

/* ══════════════════════════════════════════════════
   PLANOS (defaults — trial é sobrescrito pela config)
══════════════════════════════════════════════════ */

export const PLANOS = {
  trial: {
    id: 'trial',
    nome: 'Teste Grátis',
    preco: 0,
    duracao: 7,
    cor: '#F59E0B',
    limites: { simulacoes: 10, clientes: 5, pdfs: 5, usuarios: 1 },
    features: ['Simulador básico', '5 clientes', '5 PDFs', '1 usuário'],
  },
  starter: {
    id: 'starter',
    nome: 'Starter',
    preco: 49.90,
    duracao: 30,
    cor: '#3B82F6',
    limites: { simulacoes: 100, clientes: 50, pdfs: 50, usuarios: 3 },
    features: ['100 simulações/mês', '50 clientes', '50 PDFs', '3 usuários', 'CRM básico'],
  },
  profissional: {
    id: 'profissional',
    nome: 'Profissional',
    preco: 99.90,
    duracao: 30,
    cor: '#8B5CF6',
    limites: { simulacoes: -1, clientes: -1, pdfs: -1, usuarios: 10 },
    features: ['Simulações ilimitadas', 'Clientes ilimitados', 'PDFs ilimitados', '10 usuários', 'CRM completo', 'White Label'],
  },
  enterprise: {
    id: 'enterprise',
    nome: 'Enterprise',
    preco: 249.90,
    duracao: 30,
    cor: '#00D68F',
    limites: { simulacoes: -1, clientes: -1, pdfs: -1, usuarios: -1 },
    features: ['Tudo do Profissional', 'Usuários ilimitados', 'API access', 'Suporte prioritário', 'Onboarding dedicado'],
  },
};

/* ══════════════════════════════════════════════════
   CONFIGURAÇÕES DO SISTEMA
══════════════════════════════════════════════════ */

const CONFIG_DEFAULTS = {
  trialDias: 7,
  trialLimiteSimulacoes: 10,
  trialLimiteClientes: 5,
  trialLimitePdfs: 5,
  trialAutomatico: true,
  cadastroAberto: true,
  manutencao: false,
  manutencaoMsg: 'Sistema em manutenção. Voltamos em breve!',
};

// Cache da config para evitar leituras repetidas
let _configCache = null;
let _configCacheTime = 0;
const CONFIG_CACHE_TTL = 60000; // 1 minuto

export async function getSystemConfig() {
  // Retorna cache se ainda válido
  const now = Date.now();
  if (_configCache && (now - _configCacheTime) < CONFIG_CACHE_TTL) {
    return _configCache;
  }

  const snap = await getDoc(doc(db, 'config', 'system'));
  const data = snap.exists() ? snap.data() : {};
  _configCache = { ...CONFIG_DEFAULTS, ...data };
  _configCacheTime = now;
  return _configCache;
}

export async function updateSystemConfig(data) {
  await setDoc(doc(db, 'config', 'system'), {
    ...data,
    atualizadoEm: serverTimestamp(),
  }, { merge: true });

  // Invalidar cache
  _configCache = null;
  _configCacheTime = 0;
}

// Força invalidação do cache (útil para testes)
export function invalidateConfigCache() {
  _configCache = null;
  _configCacheTime = 0;
}

/* ══════════════════════════════════════════════════
   SUBSCRIPTION HELPERS
══════════════════════════════════════════════════ */

export async function getUserSubscription(userId) {
  const snap = await getDoc(doc(db, 'subscriptions', userId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

/**
 * Define subscription de um usuário.
 * Para trial, lê a config do sistema para duração e limites dinâmicos.
 */
export async function setUserSubscription(userId, data) {
  const now = new Date();
  const planoBase = PLANOS[data.planoId] || PLANOS.trial;

  let duracao = planoBase.duracao;
  let limites = { ...planoBase.limites };

  // Se for trial, usar valores da config do sistema
  if (data.planoId === 'trial') {
    try {
      const config = await getSystemConfig();
      duracao = config.trialDias || 7;
      limites = {
        simulacoes: config.trialLimiteSimulacoes ?? 10,
        clientes: config.trialLimiteClientes ?? 5,
        pdfs: config.trialLimitePdfs ?? 5,
        usuarios: 1,
      };
    } catch (err) {
      console.warn('Erro ao ler config do sistema, usando defaults:', err);
    }
  }

  const expira = new Date(now.getTime() + duracao * 86400000);

  await setDoc(doc(db, 'subscriptions', userId), {
    planoId: data.planoId,
    planoNome: planoBase.nome,
    preco: planoBase.preco,
    limites,
    status: data.status || 'active',
    inicioEm: serverTimestamp(),
    expiraEm: Timestamp.fromDate(expira),
    atualizadoEm: serverTimestamp(),
    ...(data.extra || {}),
  });
}

export async function cancelSubscription(userId) {
  await updateDoc(doc(db, 'subscriptions', userId), {
    status: 'cancelled',
    atualizadoEm: serverTimestamp(),
  });
}

/**
 * Ativa trial para um usuário.
 * Respeita a config do sistema para duração e limites.
 */
export async function activateTrial(userId) {
  await setUserSubscription(userId, { planoId: 'trial', status: 'trial' });
}

/* ── Check limits ── */
export function checkLimit(subscription, resource, currentCount) {
  if (!subscription) return { allowed: false, reason: 'Sem plano ativo' };
  const limite = subscription.limites?.[resource];
  if (limite === -1) return { allowed: true };
  if (currentCount >= limite) {
    return { allowed: false, reason: `Limite de ${limite} ${resource} atingido` };
  }
  return { allowed: true };
}

export function isExpired(subscription) {
  if (!subscription) return true;
  if (subscription.status === 'cancelled') return true;
  const expira = subscription.expiraEm?.toDate?.() || new Date(subscription.expiraEm);
  return new Date() > expira;
}

/* ══════════════════════════════════════════════════
   ADMIN - STATS GLOBAIS (Super Admin)
══════════════════════════════════════════════════ */

export async function getAdminStats() {
  const [usersSnap, clientsSnap, simsSnap, pdfsSnap, subsSnap, postsSnap] = await Promise.all([
    getDocs(collection(db, 'users')),
    getDocs(collection(db, 'clients')),
    getDocs(collection(db, 'simulations')),
    getDocs(collection(db, 'pdfs')),
    getDocs(collection(db, 'subscriptions')),
    getDocs(collection(db, 'blogPosts')),
  ]);

  const subs = subsSnap.docs.map(d => d.data());
  const users = usersSnap.docs.map(d => d.data());
  const now = new Date();

  return {
    totalUsuarios: usersSnap.size,
    totalClientes: clientsSnap.size,
    totalSimulacoes: simsSnap.size,
    totalPdfs: pdfsSnap.size,
    totalPosts: postsSnap.size,
    postsPublicados: postsSnap.docs.filter(d => d.data().published).length,
    roleBreakdown: {
      super_admin: users.filter(u => u.role === 'super_admin').length,
      master: users.filter(u => u.role === 'master').length,
      gestor: users.filter(u => u.role === 'gestor').length,
      usuario: users.filter(u => u.role === 'usuario').length,
    },
    planosAtivos: {
      trial: subs.filter(s => s.planoId === 'trial' && s.status !== 'cancelled').length,
      starter: subs.filter(s => s.planoId === 'starter' && s.status === 'active').length,
      profissional: subs.filter(s => s.planoId === 'profissional' && s.status === 'active').length,
      enterprise: subs.filter(s => s.planoId === 'enterprise' && s.status === 'active').length,
    },
    receitaMensal: subs
      .filter(s => s.status === 'active')
      .reduce((acc, s) => acc + (s.preco || 0), 0),
    trialsExpirados: subs.filter(s => {
      if (s.planoId !== 'trial') return false;
      const exp = s.expiraEm?.toDate?.() || new Date(s.expiraEm);
      return now > exp;
    }).length,
  };
}

/* ══════════════════════════════════════════════════
   ADMIN - LISTAR TODOS OS USUÁRIOS COM PLANO
══════════════════════════════════════════════════ */

export async function getAllUsersWithSubscription() {
  const [usersSnap, subsSnap] = await Promise.all([
    getDocs(query(collection(db, 'users'), orderBy('criadoEm', 'desc'))),
    getDocs(collection(db, 'subscriptions')),
  ]);

  const subsMap = {};
  subsSnap.docs.forEach(d => { subsMap[d.id] = d.data(); });

  return usersSnap.docs.map(d => {
    const user = { id: d.id, ...d.data() };
    user.subscription = subsMap[d.id] || null;
    return user;
  });
}

/* ══════════════════════════════════════════════════
   ADMIN - ALTERAR PLANO / ROLE
══════════════════════════════════════════════════ */

export async function adminSetUserPlan(userId, planoId) {
  await setUserSubscription(userId, { planoId, status: 'active' });
}

export async function adminCancelUserPlan(userId) {
  await cancelSubscription(userId);
}

export async function adminSetUserRole(userId, newRole) {
  const updates = {
    role: newRole,
    atualizadoEm: serverTimestamp(),
  };

  if (newRole === 'master') {
    updates.masterId = userId;
    updates.gestorId = null;
  }
  if (newRole === 'super_admin') {
    updates.masterId = null;
    updates.gestorId = null;
    updates.teamId = null;
  }

  await updateDoc(doc(db, 'users', userId), updates);
}

/* ══════════════════════════════════════════════════
   ADMIN - RECALCULAR TRIALS ATIVOS
   Aplica config atual (duração e limites) a todos
   os trials que ainda não expiraram.
══════════════════════════════════════════════════ */

export async function recalculateActiveTrials() {
  const config = await getSystemConfig();
  const subsSnap = await getDocs(
    query(collection(db, 'subscriptions'), where('planoId', '==', 'trial'))
  );

  const now = new Date();
  let updated = 0;

  for (const d of subsSnap.docs) {
    const sub = d.data();
    // Só recalcular trials que não foram cancelados
    if (sub.status === 'cancelled') continue;

    const inicioEm = sub.inicioEm?.toDate?.() || new Date(sub.inicioEm);
    const novaExpira = new Date(inicioEm.getTime() + (config.trialDias || 7) * 86400000);

    await updateDoc(doc(db, 'subscriptions', d.id), {
      expiraEm: Timestamp.fromDate(novaExpira),
      limites: {
        simulacoes: config.trialLimiteSimulacoes ?? 10,
        clientes: config.trialLimiteClientes ?? 5,
        pdfs: config.trialLimitePdfs ?? 5,
        usuarios: 1,
      },
      atualizadoEm: serverTimestamp(),
    });
    updated++;
  }

  return updated;
}